import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  getActiveDeals,
  getDealDashboard,
  getUsers,
  getSourceReport,
  bulkAssignDeals,
  bulkDeleteDeals,
} from "../api";

import DealTable from "../components/DealTable";
import "./ActiveDeals.css";

const ACTIVE_DEALS_SCROLL_KEY =
  "aranyaka_active_deals_scroll";

function formatMoney(value) {
  value = Number(value || 0);

  if (value >= 10000000) {
    return `₹${(value / 10000000)
      .toFixed(2)
      .replace(/\.00$/, "")} Cr`;
  }

  if (value >= 100000) {
    return `₹${(value / 100000)
      .toFixed(2)
      .replace(/\.00$/, "")} L`;
  }

  return `₹${value.toLocaleString("en-IN")}`;
}

export default function ActiveDeals() {
  const [searchParams, setSearchParams] =
    useSearchParams();

  // URL se existing view read hoga
  const initialPage = Math.max(
    Number(searchParams.get("page")) || 1,
    1
  );

  const initialLimit = Math.max(
    Number(searchParams.get("limit")) || 50,
    1
  );

  const initialSearch =
    searchParams.get("search") || "";

  const initialStage =
    searchParams.get("stage") || "";

  const initialStatus =
    searchParams.get("status") || "";

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] =
    useState(initialSearch);

  const [stageFilter, setStageFilter] =
    useState(initialStage);

  const [statusFilter, setStatusFilter] =
    useState(initialStatus);
    const initialAssigned =
  searchParams.get("assigned_to") || "";

const [assignedFilter, setAssignedFilter] =
  useState(initialAssigned);

const initialSource =
  searchParams.get("source") || "";

const [sourceFilter, setSourceFilter] =
  useState(initialSource);

const [sourceOptions, setSourceOptions] =
  useState([]);

  const [page, setPage] =
    useState(initialPage);

  const [limit, setLimit] =
    useState(initialLimit);

  const [totalPages, setTotalPages] =
    useState(1);

  const [totalDeals, setTotalDeals] =
    useState(0);

  const [dashboard, setDashboard] = useState({
    active_deals: 0,
    won_deals: 0,
    lost_deals: 0,
    revenue: 0,
  });
  const [stageCounts, setStageCounts] = useState({
  all: 0,
  officeMeeting: 0,
  siteVisit: 0,
  negotiation: 0,
  booked: 0,
});

  const [selectedDeals, setSelectedDeals] =
    useState([]);

  const [showBulkMenu, setShowBulkMenu] =
    useState(false);

  const [showAssignModal, setShowAssignModal] =
    useState(false);

  const [selectedUser, setSelectedUser] =
    useState("");

  const [users, setUsers] = useState([]);

  const activeDealsScrollRef = useRef(null);
  const scrollRestoredRef = useRef(false);


// ==========================================
// Load all lead sources
// ==========================================
useEffect(() => {
  async function loadSources() {
    try {
      const response = await getSourceReport();

      const rows = Array.isArray(response.data)
        ? response.data
        : [];

      const uniqueSources = [
        ...new Set(
          rows
            .map((item) =>
              String(item.source || "").trim()
            )
            .filter(Boolean)
        ),
      ].sort((a, b) =>
        a.localeCompare(b)
      );

      setSourceOptions(uniqueSources);
    } catch (error) {
      console.error(
        "LOAD SOURCE OPTIONS ERROR:",
        error
      );

      setSourceOptions([]);
    }
  }

  loadSources();
}, []);

  // ==========================================
  // Load deals
  // ==========================================
  useEffect(() => {
  loadDeals();
}, [
  page,
  limit,
  stageFilter,
  statusFilter,
  search,
  assignedFilter,
  sourceFilter,
]);

  // ==========================================
  // URL me view save karo
  // ==========================================
  useEffect(() => {
    setSearchParams(
      (currentParams) => {
        const nextParams =
          new URLSearchParams(currentParams);

        nextParams.set(
          "page",
          String(page)
        );

        nextParams.set(
          "limit",
          String(limit)
        );

        if (search.trim()) {
          nextParams.set(
            "search",
            search.trim()
          );
        } else {
          nextParams.delete("search");
        }

        if (stageFilter) {
          nextParams.set(
            "stage",
            stageFilter
          );
        } else {
          nextParams.delete("stage");
        }

        if (statusFilter) {
          nextParams.set(
            "status",
            statusFilter
          );
        } else {
          nextParams.delete("status");
        }
 if (assignedFilter) {
  nextParams.set(
    "assigned_to",
    assignedFilter
  );
} else {
  nextParams.delete("assigned_to");
}

if (sourceFilter) {
  nextParams.set(
    "source",
    sourceFilter
  );
} else {
  nextParams.delete("source");
}

        return nextParams;
      },
      {
        replace: true,
      }
    );
          }, [
          page,
          limit,
          search,
          stageFilter,
          statusFilter,
          assignedFilter,
          sourceFilter,
          setSearchParams,
          ]);

  // ============================================
  // Deal detail se back aane par scroll restore
  // ============================================
  useEffect(() => {
    if (
      loading ||
      scrollRestoredRef.current
    ) {
      return;
    }

    const savedScrollTop =
      Number(
        sessionStorage.getItem(
          ACTIVE_DEALS_SCROLL_KEY
        )
      ) || 0;

    const timer = window.setTimeout(() => {
      if (activeDealsScrollRef.current) {
        activeDealsScrollRef.current.scrollTop =
          savedScrollTop;
      }

      scrollRestoredRef.current = true;
    }, 100);

    return () =>
      window.clearTimeout(timer);
  }, [loading]);

  const resetDealsScroll = () => {
    sessionStorage.setItem(
      ACTIVE_DEALS_SCROLL_KEY,
      "0"
    );

    if (activeDealsScrollRef.current) {
      activeDealsScrollRef.current.scrollTop = 0;
    }
  };

  async function loadDeals() {
    try {
      setLoading(true);

      const [
        dealResponse,
        dashboardResponse,
        usersResponse,
      ] = await Promise.all([
        getActiveDeals(page, limit, {
  search,
  stage: stageFilter,
  status: statusFilter,
  assigned_to: assignedFilter,
  source: sourceFilter,
}),
   getDealDashboard({
  assigned_to: assignedFilter,
  source: sourceFilter,
}),
        getUsers(),
      ]);

      setDeals(
        Array.isArray(
          dealResponse.data?.data
        )
          ? dealResponse.data.data
          : []
      );

      setTotalPages(
        Number(
          dealResponse.data?.totalPages || 1
        )
      );

      setTotalDeals(
        Number(
          dealResponse.data?.total || 0
        )
      );

      setStageCounts({
  all: Number(
    dashboardResponse.data
      ?.all_stage_count || 0
  ),

  officeMeeting: Number(
    dashboardResponse.data
      ?.office_meeting_count || 0
  ),

  siteVisit: Number(
    dashboardResponse.data
      ?.site_visit_count || 0
  ),

  negotiation: Number(
    dashboardResponse.data
      ?.negotiation_count || 0
  ),

  booked: Number(
    dashboardResponse.data
      ?.booked_count || 0
  ),
});

      const usersData = Array.isArray(
        usersResponse.data
      )
        ? usersResponse.data
        : [];

      setUsers(usersData);
    } catch (error) {
      console.error(
        "LOAD ACTIVE DEALS ERROR:",
        error
      );

      setDeals([]);
      setTotalPages(1);
      setTotalDeals(0);
    } finally {
      setLoading(false);
    }
  }

  const toggleDeal = (dealId) => {
    setSelectedDeals(
      (previousSelectedDeals) =>
        previousSelectedDeals.includes(
          dealId
        )
          ? previousSelectedDeals.filter(
              (selectedId) =>
                selectedId !== dealId
            )
          : [
              ...previousSelectedDeals,
              dealId,
            ]
    );
  };

  const handleSelectAll = () => {
    if (
      deals.length > 0 &&
      selectedDeals.length === deals.length
    ) {
      setSelectedDeals([]);
      return;
    }

    setSelectedDeals(
      deals.map((deal) => deal.id)
    );
  };

  const handleBulkAssign = async () => {
    if (!selectedUser) {
      alert("Please select a user.");
      return;
    }

    if (!selectedDeals.length) {
      alert(
        "Please select at least one deal."
      );
      return;
    }

    try {
      await bulkAssignDeals(
        selectedDeals,
        selectedUser
      );

      alert(
        "Deals assigned successfully."
      );

      setShowAssignModal(false);
      setSelectedDeals([]);
      setSelectedUser("");
      setShowBulkMenu(false);

      await loadDeals();
    } catch (error) {
      console.error(
        "BULK ASSIGN DEALS ERROR:",
        error
      );

      alert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message
      );
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDeals.length === 0) {
      alert(
        "Please select at least one deal"
      );
      return;
    }

    const confirmDelete =
      window.confirm(
        `Delete ${selectedDeals.length} selected deal(s)?`
      );

    if (!confirmDelete) {
      return;
    }

    try {
      const response =
        await bulkDeleteDeals(
          selectedDeals
        );

      alert(
        response.data?.message ||
          "Selected deals deleted successfully"
      );

      setSelectedDeals([]);
      setShowBulkMenu(false);

      await loadDeals();
    } catch (error) {
      console.error(
        "DELETE DEALS ERROR:",
        error
      );

      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Could not delete selected deals"
      );
    }
  };

  const filteredDeals = (
    Array.isArray(deals) ? deals : []
  ).filter((deal) => {
    const cleanSearch =
      search.trim().toLowerCase();

    const searchMatch =
      !cleanSearch ||
      String(
        deal.deal_name || ""
      )
        .toLowerCase()
        .includes(cleanSearch) ||
      String(deal.name || "")
        .toLowerCase()
        .includes(cleanSearch) ||
      String(
        deal.project_name || ""
      )
        .toLowerCase()
        .includes(cleanSearch) ||
      String(deal.phone || "")
        .toLowerCase()
        .includes(cleanSearch);

    const stageMatch =
      !stageFilter ||
      deal.stage === stageFilter;

    const statusMatch =
      !statusFilter ||
      deal.status === statusFilter;

    return (
      searchMatch &&
      stageMatch &&
      statusMatch
    );
  });

  return (
    <div
      ref={activeDealsScrollRef}
      onScroll={(event) => {
        sessionStorage.setItem(
          ACTIVE_DEALS_SCROLL_KEY,
          String(
            event.currentTarget.scrollTop
          )
        );
      }}
      className="active-page"
      style={{
        height: "100%",
        overflow: "auto",
      }}
    >
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">
            💼 Active Deals
          </div>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="cards">
        <div className="card">
          <div className="card-title">
            Active Deals
          </div>

          <div className="card-value">
            {dashboard.active_deals}
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            Won Deals
          </div>

          <div className="card-value">
            {dashboard.won_deals}
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            Lost Deals
          </div>

          <div className="card-value">
            {dashboard.lost_deals}
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            Revenue
          </div>

          <div className="card-value">
            {formatMoney(
              dashboard.revenue
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="toolbar">
        <input
          placeholder="🔍 Search Client..."
          value={search}
          onChange={(event) => {
            resetDealsScroll();
            setSearch(
              event.target.value
            );
          }}
          style={{
            padding: 10,
            width: 260,
            borderRadius: 8,
            border: "1px solid #ddd",
          }}
        />

        <select
          value={stageFilter}
          onChange={(event) => {
            resetDealsScroll();
            setStageFilter(
              event.target.value
            );
          }}
          style={{
            padding: 10,
            borderRadius: 8,
          }}
        >
          <option value="">
            All Stages
          </option>

          <option value="Office Meeting">
            Office Meeting
          </option>

          <option value="Site Visit">
            Site Visit
          </option>
          <option value="Negotiation">Negotiation</option>

          <option value="Booked">
            Booked
          </option>
        </select>
        

        <select
          value={statusFilter}
          onChange={(event) => {
            resetDealsScroll();
            setStatusFilter(
              event.target.value
            );
          }}
          style={{
            padding: 10,
            borderRadius: 8,
          }}
        >
          <option value="">
            All Status
          </option>

          <option value="Active">
            Active
          </option>

          <option value="Won">
            Won
          </option>

          <option value="Lost">
            Lost
          </option>
        </select>
      
<select
  value={assignedFilter}
  onChange={(event) => {
    resetDealsScroll();
    setAssignedFilter(event.target.value);
    setPage(1);
  }}
  style={{
    padding: 10,
    borderRadius: 8,
    minWidth: 220,
  }}
>
  <option value="">
    All Sales Managers / Team Leads
  </option>

  {users
    .filter(
      (user) =>
        user.role === "sales_manager" ||
        user.role === "team_lead"
    )
    .sort((a, b) =>
      String(a.name || "").localeCompare(
        String(b.name || "")
      )
    )
    .map((user) => (
      <option
        key={user.id}
        value={user.id}
      >
        {user.name}
      </option>
    ))}
</select>

<select
  value={sourceFilter}
  onChange={(event) => {
    resetDealsScroll();
    setSourceFilter(event.target.value);
    setPage(1);
  }}
  style={{
    padding: 10,
    borderRadius: 8,
    minWidth: 180,
  }}
>
  <option value="">
    All Sources
  </option>

  {sourceOptions.map((source) => (
    <option
      key={source}
      value={source}
    >
      {source}
    </option>
  ))}
</select>
</div>
      {/* Pagination + Stage Counts */}
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 18,
    padding: "10px 0",
    marginBottom: 10,
    background: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 20,
    whiteSpace: "nowrap",
    overflowX: "auto",
  }}
>
  {/* Showing */}
  <div style={{ flexShrink: 0 }}>
    Showing{" "}
    {totalDeals > 0
      ? (page - 1) * limit + 1
      : 0}{" "}
    -{" "}
    {Math.min(
      page * limit,
      totalDeals
    )}{" "}
    of {totalDeals} Deals
  </div>

  {/* Show */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    }}
  >
    Show

    <select
      value={limit}
      onChange={(event) => {
        resetDealsScroll();

        setLimit(
          Number(event.target.value)
        );

        setPage(1);
      }}
      style={{
        marginLeft: 8,
        padding: "7px 10px",
        borderRadius: 6,
        cursor: "pointer",
      }}
    >
      <option value={30}>30</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
      <option value={200}>200</option>
      <option value={500}>500</option>
      <option value={1000}>1000</option>
    </select>
  </div>

  {/* Stage Counts */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flex: 1,
      minWidth: 0,
    }}
  >
    <button
      type="button"
      onClick={() => {
        resetDealsScroll();
        setStageFilter("");
        setPage(1);
      }}
      style={{
        padding: "7px 14px",
        borderRadius: 20,
        border:
          stageFilter === ""
            ? "1px solid #159570"
            : "1px solid #ddd",
        background:
          stageFilter === ""
            ? "#159570"
            : "#fff",
        color:
          stageFilter === ""
            ? "#fff"
            : "#333",
        cursor: "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      All ({stageCounts.all})
    </button>

    <button
      type="button"
      onClick={() => {
        resetDealsScroll();
        setStageFilter("Office Meeting");
        setPage(1);
      }}
      style={{
        padding: "7px 14px",
        borderRadius: 20,
        border:
          stageFilter === "Office Meeting"
            ? "1px solid #159570"
            : "1px solid #ddd",
        background:
          stageFilter === "Office Meeting"
            ? "#159570"
            : "#fff",
        color:
          stageFilter === "Office Meeting"
            ? "#fff"
            : "#333",
        cursor: "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Office Meeting ({stageCounts.officeMeeting})
    </button>

    <button
      type="button"
      onClick={() => {
        resetDealsScroll();
        setStageFilter("Site Visit");
        setPage(1);
      }}
      style={{
        padding: "7px 14px",
        borderRadius: 20,
        border:
          stageFilter === "Site Visit"
            ? "1px solid #159570"
            : "1px solid #ddd",
        background:
          stageFilter === "Site Visit"
            ? "#159570"
            : "#fff",
        color:
          stageFilter === "Site Visit"
            ? "#fff"
            : "#333",
        cursor: "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Site Visit ({stageCounts.siteVisit})
    </button>

    <button
      type="button"
      onClick={() => {
        resetDealsScroll();
        setStageFilter("Negotiation");
        setPage(1);
      }}
      style={{
        padding: "7px 14px",
        borderRadius: 20,
        border:
          stageFilter === "Negotiation"
            ? "1px solid #159570"
            : "1px solid #ddd",
        background:
          stageFilter === "Negotiation"
            ? "#159570"
            : "#fff",
        color:
          stageFilter === "Negotiation"
            ? "#fff"
            : "#333",
        cursor: "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Negotiation ({stageCounts.negotiation})
    </button>

    <button
      type="button"
      onClick={() => {
        resetDealsScroll();
        setStageFilter("Booked");
        setPage(1);
      }}
      style={{
        padding: "7px 14px",
        borderRadius: 20,
        border:
          stageFilter === "Booked"
            ? "1px solid #159570"
            : "1px solid #ddd",
        background:
          stageFilter === "Booked"
            ? "#159570"
            : "#fff",
        color:
          stageFilter === "Booked"
            ? "#fff"
            : "#333",
        cursor: "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Booked ({stageCounts.booked})
    </button>
  </div>

  {/* Pagination */}
  <div
    style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      flexShrink: 0,
    }}
  >
    <button
      type="button"
      disabled={page <= 1}
      onClick={() => {
        resetDealsScroll();

        setPage(
          (currentPage) =>
            currentPage - 1
        );
      }}
    >
      Previous
    </button>

    <span>
      Page {page} of {totalPages}
    </span>

    <button
      type="button"
      disabled={
        page >= totalPages
      }
      onClick={() => {
        resetDealsScroll();

        setPage(
          (currentPage) =>
            currentPage + 1
        );
      }}
    >
      Next
    </button>
  </div>
</div>

                    {loading ? (
                      <div className="loading-screen">
                        <div className="spinner" />
                      </div>
                    ) : (
                      <DealTable
                deals={filteredDeals}
                page={page}
                limit={limit}
                selectedDeals={selectedDeals}
                toggleDeal={toggleDeal}
                handleSelectAll={handleSelectAll}
                showBulkMenu={showBulkMenu}
                setShowBulkMenu={setShowBulkMenu}
                setShowAssignModal={setShowAssignModal}
                handleDeleteSelected={handleDeleteSelected}
              />
                    )}

                    {/* Assign Modal */}
                    {showAssignModal && (
                      <div
                        style={{
                          position: "fixed",
                          inset: 0,
                          background:
                            "rgba(0,0,0,.35)",
                          display: "flex",
                          justifyContent:
                            "center",
                          alignItems: "center",
                          zIndex: 9999,
                        }}
                      >
                        <div
                          style={{
                            width: 420,
                            background: "#fff",
                            borderRadius: 10,
                            padding: 25,
                          }}
                        >
                          <h3>Assign Deals</h3>

                          <select
                            value={selectedUser}
                            onChange={(event) =>
                              setSelectedUser(
                                event.target.value
                              )
                            }
                            style={{
                              width: "100%",
                              padding: 10,
                              marginTop: 20,
                              marginBottom: 20,
                            }}
                          >
                            <option value="">
                              Select User
                            </option>

                            {users.map((user) => (
                              <option
                                key={user.id}
                                value={user.id}
                              >
                                {user.name}
                              </option>
                            ))}
                          </select>

                          <div
                            style={{
                              display: "flex",
                              justifyContent:
                                "flex-end",
                              gap: 10,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setShowAssignModal(
                                  false
                                );
                                setSelectedUser("");
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              onClick={
                                handleBulkAssign
                              }
                              style={{
                                background:
                                  "#16A34A",
                                color: "#fff",
                                border: "none",
                                padding: "10px 18px",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                                    >
                        Assign
                      </button>
                    </div>
                </div>
                </div>
              )}
            </div>
  );
}